import React from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { media } from "../../../data/media";
import type { ServiceItem } from "../../../types/service";
type ServiceCardProps = {
  service: ServiceItem;
  index: number;
  total: number;
  active: boolean;
  onActivate: (index: number) => void;
};

/**
 * One capability row. All seven sit on screen at once, so the row stays compact and only the
 * active one opens. Hover, focus or tap makes it active, which also drives the media card.
 */
export function ServiceCard({
  service,
  index,
  total,
  active,
  onActivate
}: ServiceCardProps) {
  const reduceMotion = useReducedMotion();
  const plate = (media as Record<string, string>)[service.plate] ?? media.archivePlate;
  const panelId = `service-panel-${service.id}`;
  return <motion.li initial={{
    opacity: 0,
    y: 16
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: "-6%"
  }} transition={{
    duration: 0.5,
    delay: Math.min(index * 0.05, 0.3),
    ease: [0.16, 1, 0.3, 1]
  }} className={`relative overflow-hidden rounded-[20px] border transition-colors duration-500 ${active ? "border-[#54f6c6]/45 bg-[#0c1312]" : "border-white/10 bg-[#0a0f0e] hover:border-white/25"}`} data-id="element-194" data-mp="2x07k4yhta85z">
      <span aria-hidden="true" className={`absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#54f6c6]/70 to-transparent transition-opacity duration-500 ${active ? "opacity-100" : "opacity-0"}`} data-id="element-195" data-mp="428b9dz653vk" />

      <button type="button" onMouseEnter={() => onActivate(index)} onFocus={() => onActivate(index)} onClick={() => onActivate(index)} aria-current={active} aria-controls={panelId} data-cursor="View" className="flex w-full items-center gap-4 px-5 py-5 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#54f6c6] sm:gap-6 sm:px-7" data-id="element-196" data-mp="iq354xowxea8">
        <span className={`font-mono text-[11px] uppercase tracking-[0.18em] transition-colors duration-300 ${active ? "text-[#54f6c6]" : "text-white/35"}`} data-id="element-197" data-mp="2stiozkszdh7j">
          {service.number}
        </span>

        <span className="min-w-0 flex-1" data-id="element-198" data-mp="32kdd6upseqvm">
          <span className="block text-[clamp(1.15rem,2.2vw,1.6rem)] font-semibold leading-[1.12] tracking-[-0.04em] text-white" data-id="element-199" data-mp="2xs2efv6s9b4n">
            {service.title}
          </span>
          <span className="mt-2 block font-mono text-[11px] uppercase tracking-[0.18em] text-white/40" data-id="element-200" data-mp="3cj2xeu55onf1">{service.label}</span>
        </span>

        <ArrowUpRight aria-hidden="true" className={`h-4 w-4 shrink-0 transition-[color,transform] duration-300 ${active ? "translate-x-0.5 -translate-y-0.5 text-[#54f6c6]" : "text-white/30"}`} data-id="element-201" data-mp="3li6gvz88j4u2" />
      </button>

      {/* Mobile / tablet only: the image comes first, with the detail sitting directly beneath it.
          On desktop this same detail lives under the companion media card instead. */}
      <AnimatePresence initial={false} data-id="element-202" data-mp="1n9x3hwai01q">
        {active && <motion.div key="panel" id={panelId} initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: "auto",
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: reduceMotion ? 0.2 : 0.45,
        ease: [0.16, 1, 0.3, 1]
      }} className="overflow-hidden lg:hidden" data-id="element-203" data-mp="6kjkks04c1mx">
            <div className="px-5 pb-6 sm:px-7 sm:pb-7" data-id="element-204" data-mp="3k07srcm1u6q3">
              <div className="overflow-hidden rounded-[14px] border border-white/10 bg-black" data-id="element-205" data-mp="3k22bck9ulmr0">
                <div className="relative aspect-video" data-id="element-206" data-mp="362au8ao1bq8s">
                  {service.videoUrl && !reduceMotion ? <video className="h-full w-full object-cover" src={service.videoUrl} poster={plate} autoPlay muted loop playsInline preload="none" tabIndex={-1} aria-hidden="true" data-id="element-207" data-mp="3a4e793qcr2w6" /> : <img src={plate} alt="" aria-hidden="true" className="h-full w-full object-cover" loading="lazy" data-id="element-208" data-mp="188lz1usf5foo" />}
                  <div aria-hidden="true" className="absolute inset-0 bg-[linear-gradient(to_top,rgba(5,8,8,0.6),transparent_60%)]" data-id="element-209" data-mp="ekxze3qa9s94" />
                  <span aria-hidden="true" className="absolute bottom-3 left-3 font-mono text-[11px] uppercase tracking-[0.18em] text-white/75" data-id="element-210" data-mp="9kjtosc5mue9">
                    Loop {service.number} / {String(total).padStart(2, "0")}
                  </span>
                </div>
              </div>

              <p className="mt-6 max-w-xl text-sm leading-7 text-white/65 md:text-[15px]" data-id="element-211" data-mp="3rzc8bvtmkj42">{service.description}</p>

              <ul className="mt-5 flex flex-wrap gap-2" data-id="element-212" data-mp="3n1amj34te5ow">
                {service.capabilities.map(capability => <li key={capability} className="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.035] px-3.5 py-2 text-[13px] text-white/80" data-id="element-213" data-mp="3i03h3aq59ile">
                    <span aria-hidden="true" className="h-1 w-1 shrink-0 rounded-full bg-[#54f6c6]" data-id="element-214" data-mp="1ojqsmqdvudqm" />
                    {capability}
                  </li>)}
              </ul>
            </div>
          </motion.div>}
      </AnimatePresence>
    </motion.li>;
}